<template>
  <div class="flex-1 overflow-y-auto bg-gray-50 dark:bg-[#0d1117]">
    <div class="grid grid-cols-3 gap-6 p-6 max-w-6xl mx-auto">
      <div
        v-for="month in months"
        :key="month.index"
        class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 p-4"
      >
        <!-- Month name -->
        <h3
          class="text-sm font-semibold mb-3 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          :class="month.isCurrentMonth ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-200'"
          @click="emit('month-click', month.firstDay)"
        >
          {{ month.name }}
        </h3>

        <!-- Day-of-week headers -->
        <div class="grid grid-cols-7 mb-1">
          <span
            v-for="d in ['S','M','T','W','T','F','S']"
            :key="d"
            class="text-center text-[10px] font-medium text-gray-400 dark:text-gray-500 py-0.5"
          >{{ d }}</span>
        </div>

        <!-- Day cells -->
        <div class="grid grid-cols-7">
          <button
            v-for="day in month.days"
            :key="day.key"
            class="aspect-square flex items-center justify-center text-[11px] rounded-full transition-colors relative"
            :class="[
              !day.currentMonth  ? 'text-gray-300 dark:text-gray-700 cursor-default' : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700',
              day.isToday        ? 'bg-blue-600 text-white font-semibold hover:bg-blue-700' : '',
              day.currentMonth && !day.isToday ? 'text-gray-700 dark:text-gray-300' : '',
            ]"
            :disabled="!day.currentMonth"
            @click="day.currentMonth && emit('day-click', day.date)"
          >
            {{ day.dayNum }}
            <!-- Booking dot -->
            <span
              v-if="day.currentMonth && !day.isToday && hasBookings(day.date)"
              class="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-blue-500"
            ></span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue'
import { useCalendarStore } from 'stores/calendar.js'

const props = defineProps({
  selectedDate: { type: Date, required: true },
})

const emit = defineEmits(['day-click', 'month-click'])

const store = useCalendarStore()
const today = new Date(); today.setHours(0, 0, 0, 0)

const year = computed(() => props.selectedDate.getFullYear())

const months = computed(() => {
  const currentMonth = today.getMonth()
  const currentYear  = today.getFullYear()

  return Array.from({ length: 12 }, (_, monthIndex) => {
    const firstDay = new Date(year.value, monthIndex, 1)
    const lastDay  = new Date(year.value, monthIndex + 1, 0)
    const days     = []

    // Leading padding from previous month
    for (let i = 0; i < firstDay.getDay(); i++) {
      const d = new Date(year.value, monthIndex, 1 - firstDay.getDay() + i)
      d.setHours(0, 0, 0, 0)
      days.push({ date: d, dayNum: d.getDate(), currentMonth: false, isToday: false, key: `p${monthIndex}-${i}` })
    }

    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const d = new Date(year.value, monthIndex, i); d.setHours(0, 0, 0, 0)
      days.push({ date: d, dayNum: i, currentMonth: true, isToday: d.getTime() === today.getTime(), key: `c${monthIndex}-${i}` })
    }

    // Trailing padding
    const rem = 7 - (days.length % 7)
    if (rem < 7) {
      for (let i = 1; i <= rem; i++) {
        const d = new Date(year.value, monthIndex + 1, i); d.setHours(0, 0, 0, 0)
        days.push({ date: d, dayNum: i, currentMonth: false, isToday: false, key: `n${monthIndex}-${i}` })
      }
    }

    return {
      index:          monthIndex,
      name:           firstDay.toLocaleDateString('en-US', { month: 'long' }),
      firstDay,
      days,
      isCurrentMonth: monthIndex === currentMonth && year.value === currentYear,
    }
  })
})

function hasBookings(date) {
  const dayEnd = new Date(date); dayEnd.setHours(23, 59, 59, 999)
  return store.filteredBookings.some(b => {
    const start = new Date(b.start_at ?? b.start)
    return start >= date && start <= dayEnd
  })
}

async function fetchYear(date) {
  const y = date.getFullYear()
  await store.fetchBookings(`${y}-01-01`, `${y}-12-31`)
}

onMounted(() => fetchYear(props.selectedDate))
watch(() => props.selectedDate, fetchYear)
</script>
